import { ApiReportSectionFieldsFieldTypeChoices as FieldType } from "@api/graphql/types-and-hooks";
import { parseJson } from "@components/FieldComponent/utils";
import {
  getCheckedOptions,
  parseCheckboxContent,
  parseRadioContent,
  parseSelectContent,
} from "@components/ReportField/inputs/choiceContent";
import { formatDisplayDatetime, parseStoredDatetime } from "@components/ReportField/inputs/dateFieldUtils";

const toSingleItem = (answer: string | null | undefined): string[] => (answer ? [answer] : []);

interface ChoiceContent {
  options: string[];
  items: string[];
}

const parseChoiceContent = (fieldType: FieldType | undefined, value: string): ChoiceContent | undefined => {
  if (fieldType === FieldType.Select) {
    const result = parseSelectContent(value);
    return result.success ? { options: result.data.options, items: toSingleItem(result.data.answer) } : undefined;
  }
  if (fieldType === FieldType.Radio) {
    const result = parseRadioContent(value);
    return result.success ? { options: result.data.options, items: toSingleItem(result.data.answer) } : undefined;
  }
  if (fieldType === FieldType.Checkbox) {
    const result = parseCheckboxContent(value);
    return result.success ? { options: result.data.options, items: getCheckedOptions(result.data) } : undefined;
  }
  return undefined;
};

/** The answers a Select, Radio or Checkbox field shows as option labels; undefined for any other value. */
export const toChoiceItems = (fieldType: FieldType | undefined, value: string): string[] | undefined =>
  parseChoiceContent(fieldType, value)?.items;

/** What a clinician reads on the report for a field's stored value: the answer, not its JSON or timestamp. */
export const toReadableValue = (fieldType: FieldType | undefined, value: string): string => {
  const items = toChoiceItems(fieldType, value);
  if (items) return items.join(", ");
  if (fieldType === FieldType.Datetime) return formatDisplayDatetime(parseStoredDatetime(value)) || value;
  return value;
};

/** Whether two stored values hold the same answer. Datetimes compare as instants, since the report shows them only to the minute. */
export const isSameValue = (fieldType: FieldType | undefined, value: string, otherValue: string): boolean => {
  if (fieldType === FieldType.Datetime) {
    const date = parseStoredDatetime(value);
    const otherDate = parseStoredDatetime(otherValue);
    if (date && otherDate) return date.getTime() === otherDate.getTime();
  }
  return toReadableValue(fieldType, value) === toReadableValue(fieldType, otherValue);
};

/** Whether the field's options now still hold every answer the suggestion picks. An empty field has no options of its own, so any answer fits. */
export const fitsCurrentOptions = (
  fieldType: FieldType | undefined,
  currentValue: string,
  proposedValue: string,
): boolean => {
  const current = currentValue ? parseChoiceContent(fieldType, currentValue) : undefined;
  const proposedItems = toChoiceItems(fieldType, proposedValue);
  return !current || !proposedItems || proposedItems.every((item) => current.options.includes(item));
};

/** A suggestion carries the options the field had then; applying it must change the answer only. The suggestion's options are kept when the field is empty or its options no longer hold the suggested answer. */
export const toAppliedValue = (
  fieldType: FieldType | undefined,
  currentValue: string,
  proposedValue: string,
): string => {
  if (
    !currentValue ||
    !toChoiceItems(fieldType, currentValue) ||
    !toChoiceItems(fieldType, proposedValue) ||
    !fitsCurrentOptions(fieldType, currentValue, proposedValue)
  )
    return proposedValue;
  return JSON.stringify({
    ...parseJson<object>(currentValue),
    answer: parseJson<{ answer: unknown }>(proposedValue)?.answer,
  });
};
